import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Mail, Send, CheckCircle, Instagram, Twitter, Facebook, Youtube, Lock, Unlock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const ComingSoonPage = ({ hasBypassPassword, onBypass }) => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    title: "Coming Soon",
    subtitle: "We're working hard to bring you something amazing",
    description: "Our new platform is under construction. Sign up to be notified when we launch!",
    logo_url: "",
    background_image: "",
    show_countdown: false,
    launch_date: "",
    social_links: {}
  });
  
  // Bypass password state
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [bypassPassword, setBypassPassword] = useState("");
  const [bypassError, setBypassError] = useState("");
  const [bypassLoading, setBypassLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/settings/coming-soon`);
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setSettings(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.error("Error fetching coming soon settings:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await fetch(`${API}/settings/coming-soon/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      setSubmitted(true);
      setEmail("");
    } catch (error) {
      console.error("Error subscribing:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleBypassSubmit = async (e) => {
    e.preventDefault();
    if (!bypassPassword) return;
    
    setBypassLoading(true);
    setBypassError("");
    
    try {
      const response = await fetch(`${API}/settings/coming-soon/verify-bypass`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: bypassPassword })
      });
      
      if (response.ok) {
        if (onBypass) onBypass();
      } else {
        setBypassError("Invalid password");
      }
    } catch (error) {
      console.error("Error verifying bypass:", error);
      setBypassError("Failed to verify password");
    } finally {
      setBypassLoading(false);
    }
  };

  // Calculate countdown
  const getCountdown = () => {
    if (!settings.launch_date) return null;
    
    const launch = new Date(settings.launch_date).getTime();
    const now = new Date().getTime();
    const diff = launch - now;
    
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds };
  };

  const [countdown, setCountdown] = useState(getCountdown());

  useEffect(() => {
    if (!settings.show_countdown || !settings.launch_date) return;
    
    const interval = setInterval(() => {
      setCountdown(getCountdown());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [settings.show_countdown, settings.launch_date]);

  return (
    <div 
      className="min-h-screen bg-black flex flex-col items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: settings.background_image ? `url(${settings.background_image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          style={{ top: '10%', left: '10%' }}
        />
        <motion.div
          className="absolute w-64 h-64 bg-orange-600/10 rounded-full blur-3xl"
          animate={{
            x: [0, -80, 0],
            y: [0, 80, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          style={{ bottom: '20%', right: '15%' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
        {/* Logo */}
        {settings.logo_url ? (
          <motion.img
            src={settings.logo_url}
            alt="Logo"
            className="h-16 md:h-20 mx-auto mb-8 object-contain"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
        ) : (
          <motion.div
            className="flex items-center justify-center gap-2 mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
              <span className="text-black font-bold text-xl">A</span>
            </div>
            <span className="font-heading text-2xl text-white uppercase tracking-wider">ASN</span>
          </motion.div>
        )}

        {/* Title */}
        <motion.h1
          className="font-heading text-4xl md:text-6xl lg:text-7xl uppercase text-white mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {settings.title.split(' ').map((word, i) => (
            <span key={i} className={i === 1 ? 'text-orange-500' : ''}>
              {word}{' '}
            </span>
          ))}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-zinc-300 mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {settings.subtitle}
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-zinc-500 mb-8 max-w-md mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {settings.description}
        </motion.p>

        {/* Countdown */}
        {settings.show_countdown && countdown && (
          <motion.div
            className="flex items-center justify-center gap-4 md:gap-6 mb-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {[
              { value: countdown.days, label: 'Days' },
              { value: countdown.hours, label: 'Hours' },
              { value: countdown.minutes, label: 'Minutes' },
              { value: countdown.seconds, label: 'Seconds' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-2">
                  <span className="font-heading text-2xl md:text-3xl text-orange-500">
                    {String(item.value).padStart(2, '0')}
                  </span>
                </div>
                <span className="text-xs text-zinc-500 uppercase tracking-wider">{item.label}</span>
              </div>
            ))}
          </motion.div>
        )}

        {/* Email Signup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {submitted ? (
            <div className="flex items-center justify-center gap-2 text-green-500 bg-green-500/10 border border-green-500/30 px-6 py-4">
              <CheckCircle className="w-5 h-5" />
              <span>Thanks! We'll notify you when we launch.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-zinc-900 border border-zinc-800 text-white pl-12 pr-4 py-3 focus:border-orange-500 outline-none transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-orange-500 text-black font-bold px-6 py-3 hover:bg-orange-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Notify Me
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>

        {/* Social Links */}
        {Object.keys(settings.social_links || {}).length > 0 && (
          <motion.div
            className="flex items-center justify-center gap-4 mt-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {settings.social_links.instagram && (
              <a href={settings.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-orange-500 transition-colors">
                <Instagram className="w-6 h-6" />
              </a>
            )}
            {settings.social_links.twitter && (
              <a href={settings.social_links.twitter} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-orange-500 transition-colors">
                <Twitter className="w-6 h-6" />
              </a>
            )}
            {settings.social_links.facebook && (
              <a href={settings.social_links.facebook} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-orange-500 transition-colors">
                <Facebook className="w-6 h-6" />
              </a>
            )}
            {settings.social_links.youtube && (
              <a href={settings.social_links.youtube} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-orange-500 transition-colors">
                <Youtube className="w-6 h-6" />
              </a>
            )}
          </motion.div>
        )}

        {/* Access Button (if bypass password is set) */}
        {hasBypassPassword && (
          <motion.div
            className="mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.65 }}
          >
            <button
              onClick={() => setShowBypassModal(true)}
              className="flex items-center gap-2 mx-auto text-zinc-600 hover:text-orange-500 transition-colors text-sm"
            >
              <Lock className="w-4 h-4" />
              Have an access code?
            </button>
          </motion.div>
        )}

        {/* Clock Icon */}
        <motion.div
          className="mt-12 text-zinc-700"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <Clock className="w-8 h-8 mx-auto" />
        </motion.div>
      </div>
      
      {/* Bypass Password Modal */}
      {showBypassModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-white/10 p-8 max-w-md w-full"
          >
            <div className="flex items-center gap-3 mb-6">
              <Unlock className="w-6 h-6 text-orange-500" />
              <h3 className="text-xl font-bold text-white">Enter Access Code</h3>
            </div>
            
            <form onSubmit={handleBypassSubmit} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={bypassPassword}
                  onChange={(e) => setBypassPassword(e.target.value)}
                  placeholder="Enter your access code"
                  className="w-full bg-black border border-zinc-700 text-white px-4 py-3 focus:border-orange-500 outline-none"
                  autoFocus
                />
                {bypassError && (
                  <p className="text-red-500 text-sm mt-2">{bypassError}</p>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowBypassModal(false)}
                  className="flex-1 px-4 py-3 bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bypassLoading || !bypassPassword}
                  className="flex-1 px-4 py-3 bg-orange-500 text-black font-bold hover:bg-orange-400 transition-colors disabled:opacity-50"
                >
                  {bypassLoading ? "Verifying..." : "Access Site"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ComingSoonPage;
